import { HTMLAttributes } from 'react'
import {
  DisplayProps,
  LayoutProps,
  MarginProps,
  PositionProps,
} from 'styled-system'

import { TestIdProps } from 'designSystem/types/propTypes'

import { COLOR } from 'designSystem/foundations/constants/color'
import { SIZE } from 'designSystem/foundations/constants/size'

import PDSIcon, { IconName } from '../PDSIcon/PDSIcon'
import INTERNALxTag from './INTERNALxTag'

type INTERNAL_NumberTagSize = 'sm' | 'md' | 'lg'
type INTERNAL_NumberVariant = 'alert' | 'neutral' | 'success'
type INTERNAL_Direction = 'down' | 'none' | 'up'

type BaseProps = HTMLAttributes<HTMLDivElement> &
  DisplayProps &
  LayoutProps &
  MarginProps &
  PositionProps &
  TestIdProps

type Props = {
  decimals?: number
  direction?: INTERNAL_Direction
  iconDown: IconName
  iconUp: IconName
  isNeutral?: boolean
  showDirection?: boolean
  size?: INTERNAL_NumberTagSize
  symbol?: string
  value: number
} & BaseProps

export default function INTERNALxNumberTag({
  decimals,
  direction,
  iconDown,
  iconUp,
  isNeutral,
  showDirection = true,
  size = 'md',
  symbol,
  testId,
  value,
  ...props
}: Props) {
  let directionType: INTERNAL_Direction = 'none'
  let icon: IconName | null = iconUp

  if (value < 0) {
    directionType = 'down'
    icon = iconDown
  } else if (value > 0) {
    directionType = 'up'
    icon = iconUp
  }

  // override symbols and values
  switch (direction) {
    case 'down':
      directionType = 'down'
      icon = iconDown
      break
    case 'up':
      directionType = 'up'
      icon = iconUp
      break
    case 'none':
      directionType = 'none'
      icon = iconUp
      break
  }

  // override the final theme via directionType
  if (isNeutral) {
    directionType = 'none'
  }

  // override icon displays
  if (!showDirection) {
    icon = null
  }

  let displayValue: number | string = Math.abs(value)

  if (typeof decimals === 'number' && decimals >= 0) {
    displayValue = displayValue.toFixed(decimals)
  }

  return (
    <INTERNALxTag
      {...props}
      justifyContent="center"
      paddingLeft={SIZE.s4}
      paddingRight={icon ? SIZE.s8 : SIZE.s4}
      showBorder={false}
      size={size}
      testId={testId}
      variant={NUMBER_TAG_VARIANT[directionType].variant}
    >
      {icon && (
        <PDSIcon
          color={NUMBER_TAG_VARIANT[directionType].iconColor}
          name={icon}
          size="sm"
        />
      )}
      {displayValue}
      {symbol}
    </INTERNALxTag>
  )
}

type INTERNAL_NumberVariantMap = {
  [key: string]: {
    variant: INTERNAL_NumberVariant
    iconColor: string
  }
}
const NUMBER_TAG_VARIANT: INTERNAL_NumberVariantMap = {
  up: {
    iconColor: COLOR.success500,
    variant: 'success',
  },
  none: {
    iconColor: COLOR.neutral500,
    variant: 'neutral',
  },
  down: {
    iconColor: COLOR.alert500,
    variant: 'alert',
  },
}
